// Таблицата на справката „Изписвания": ниски редове на един ред. Широк екран —
// № · Дата · Вид и причина (коментарът приглушено до нея) · Склад · Въвел ·
// Продукти · Нето · Бруто · Печат ⋯; лаптоп/таблет — № · Дата · Вид и причина
// · Склад · Въвел (в една клетка) · Бруто; телефон — карти. Филтрите са в
// заглавията на колоните; №/Дата/Склад/Бруто сортират. Анулираните стоят
// зачертани, а редът „Общо за периода" е без тях. ↑/↓ по редовете, Enter
// отваря документа.
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Writeoff } from "../../../../api/inventory";
import { fmtDate, fmtDateTime } from "../../components/dateTime";
import { formatMoney } from "../../components/numbers";
import { StatusPill } from "../../components/list/ListBits";
import { RowActionsMenu, type RowAction } from "../../components/list/RowActionsMenu";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import { WriteoffKindBadge } from "../../writeoff/components/WriteoffKindBadge";
import { isWriteoffAnnulled } from "../../writeoff/components/writeoffMeta";
import { ReportHead } from "../components/ReportListBits";
import {
  REPORT_TOTAL_LABEL_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "../components/reportListModel";
import type { WriteoffFilterKey, WriteoffSort, WriteoffSortKey } from "./writeoffsReportModel";

export type WriteoffsDensity = "full" | "medium" | "cards";

// Литерални класове — Tailwind сканира само пълни низове
const GRID_CLASS = {
  full: "grid grid-cols-[52px_128px_minmax(200px,1.6fr)_minmax(100px,.8fr)_minmax(90px,.7fr)_72px_96px_104px_100px] items-center gap-2.5 px-[18px]",
  medium: "grid grid-cols-[48px_92px_minmax(180px,1.5fr)_minmax(120px,1fr)_104px_100px] items-center gap-2.5 px-[18px]",
} as const;

type Props = {
  rows: Writeoff[];
  density: WriteoffsDensity;
  sort: WriteoffSort;
  onSort: (key: WriteoffSortKey) => void;
  filters: ColumnSections<WriteoffFilterKey>;
  reasonLabel: (reason: string) => string;
  totalNet: number;
  totalGross: number;
  onOpen: (writeoff: Writeoff) => void;
  onPrint: (writeoff: Writeoff) => void;
  actionsOf: (writeoff: Writeoff) => RowAction[];
  footer?: ReactNode;
};

export const WriteoffsReportTable = ({
  rows,
  density,
  sort,
  onSort,
  filters,
  reasonLabel,
  totalNet,
  totalGross,
  onOpen,
  onPrint,
  actionsOf,
  footer,
}: Props) => {
  const { t } = useTranslation();

  const numberClass = (writeoff: Writeoff) =>
    isWriteoffAnnulled(writeoff) ? "text-faint line-through" : "text-ink";
  const reasonText = (writeoff: Writeoff) =>
    writeoff.reason ? reasonLabel(writeoff.reason) : t("inventoryPage.woNoReason");
  // Вид · причина (+ „Анулиран") и коментарът приглушено — на един ред
  const kindCell = (writeoff: Writeoff, withComment: boolean) => (
    <span className="flex min-w-0 items-center gap-2">
      <WriteoffKindBadge kind={writeoff.kind} />
      <span className={`min-w-0 truncate font-medium ${numberClass(writeoff)}`}>{reasonText(writeoff)}</span>
      {isWriteoffAnnulled(writeoff) && (
        <StatusPill tone="neutral">{t("inventoryPage.statusAnnulled")}</StatusPill>
      )}
      {withComment && writeoff.comment && (
        <span className="min-w-0 truncate text-muted" title={writeoff.comment}>
          · {writeoff.comment}
        </span>
      )}
    </span>
  );

  if (density === "cards") {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((writeoff) => (
          <div
            key={writeoff.id}
            {...openableRowProps(() => onOpen(writeoff))}
            className="flex cursor-pointer flex-col gap-[5px] border-t border-shade-1 px-3.5 py-3 outline-none focus-visible:bg-fill"
          >
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">{kindCell(writeoff, false)}</div>
              <RowActionsMenu actions={actionsOf(writeoff)} compact />
            </div>
            <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
              <span className="min-w-0 truncate">
                №{writeoff.id} · {fmtDateTime(writeoff.date)} · {writeoff.warehouseName || "—"}
              </span>
              <span className={`font-sheet text-sm font-semibold ${numberClass(writeoff)}`}>
                {formatMoney(writeoff.totalValueWithVat || 0)}
              </span>
            </div>
          </div>
        ))}
        {footer}
      </div>
    );
  }

  const grid = GRID_CLASS[density];
  const full = density === "full";
  const head = (
    label: string,
    options: { sortKey?: WriteoffSortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} {...options} />;

  return (
    <div className="overflow-x-auto">
      <div className={full ? "min-w-[1074px]" : "min-w-[730px]"} onKeyDown={moveRowFocus}>
        <div className={`${grid} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("№", { sortKey: "doc" })}
          {head(t("inventoryPage.colDate"), { sortKey: "date" })}
          {head(t("inventoryPage.cfKindReason"), {
            filters: sectionList(filters.kinds, filters.reasons, filters.statuses),
          })}
          {full ? (
            <>
              {head(t("inventoryPage.colWarehouse"), { sortKey: "warehouse", filters: sectionList(filters.warehouses) })}
              {head(t("inventoryPage.filterEnteredBy"), { filters: sectionList(filters.users) })}
              {head(t("inventoryPage.colProducts"), { align: "right" })}
              {head(t("inventoryPage.colNet"), { align: "right" })}
            </>
          ) : (
            head(`${t("inventoryPage.colWarehouse")} · ${t("inventoryPage.filterEnteredBy")}`, {
              sortKey: "warehouse",
              filters: sectionList(filters.warehouses, filters.users),
            })
          )}
          {head(t("inventoryPage.colGross"), { sortKey: "sum", align: "right", filters: sectionList(filters.gross) })}
          <span />
        </div>
        {rows.map((writeoff) => (
          <div
            key={writeoff.id}
            {...openableRowProps(() => onOpen(writeoff))}
            className={`${grid} ${REPORT_OPENABLE_ROW_CLASS}`}
          >
            <span className="font-sheet text-body">{writeoff.id}</span>
            <span className="whitespace-nowrap font-sheet text-ink-2">
              {full ? fmtDateTime(writeoff.date) : fmtDate(writeoff.date)}
            </span>
            {kindCell(writeoff, full)}
            {full ? (
              <>
                <span className="truncate text-ink">{writeoff.warehouseName || "—"}</span>
                <span className="truncate text-ink-2">{writeoff.createdByName || "—"}</span>
                <span className="text-right font-sheet text-ink-2">{writeoff.productsCount ?? 0}</span>
                <span className={`text-right font-sheet ${numberClass(writeoff)}`}>
                  {formatMoney(writeoff.totalValueNoVat || 0)}
                </span>
              </>
            ) : (
              <span className="min-w-0 truncate text-ink">
                {writeoff.warehouseName || "—"}
                <span className="text-muted"> · {writeoff.createdByName || "—"}</span>
              </span>
            )}
            <span className={`text-right font-sheet font-semibold ${numberClass(writeoff)}`}>
              {formatMoney(writeoff.totalValueWithVat || 0)}
            </span>
            <RowActionsMenu actions={actionsOf(writeoff)} onPrint={() => onPrint(writeoff)} dense />
          </div>
        ))}
        {footer}
        {rows.length > 0 && (
          <div className={`${grid} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_TOTAL_LABEL_CLASS} ${full ? "col-span-6" : "col-span-4"}`}>
              {t("inventoryPage.periodTotalActive")}
            </span>
            {full && (
              <span className="text-right font-sheet font-semibold text-ink">{formatMoney(totalNet)}</span>
            )}
            <span className="text-right font-sheet font-bold text-brand-deep-ink">
              {formatMoney(totalGross)}
            </span>
            <span />
          </div>
        )}
      </div>
    </div>
  );
};

export default WriteoffsReportTable;
